// @flow
import * as React from 'react';

import type { CartConfiguration } from '@gandi/cart';
import { type TranslatorProps, useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import type { CreateOrTransfer } from '../../../../types';
import type { TemplateKind } from '../types';

import BottomLinks from './BottomLinks';

function getDescription({
  translator,
  process,
  templateKind,
}: {
  translator: TranslatorProps,
  process: CreateOrTransfer,
  templateKind: TemplateKind,
}) {
  const { __ } = translator;
  switch (true) {
    case process === 'transfer' && templateKind === 'default':
      return (
        <Base
          tagName="p"
          dangerouslySetInnerHTML={__(
            "By default, we don't apply any configuration changes during a transfer. **We will keep your domains' current nameservers but some registrars may shut down the access to their nameservers when the transfer is completed.** If you want to specify other nameservers, Gandi's LiveDNS for example, you can apply another configuration.",
            { withMarkdown: true },
          )}
        />
      );
    case process === 'transfer' && templateKind === 'custom':
      return __(
        'The following custom configuration will be applied during the transfer.',
      );
    case process === 'transfer' && templateKind === 'predefined':
      return __(
        'You have predefined a configuration for your domains transfers. This configuration will be applied during the transfer.',
      );
    case process === 'create' && templateKind === 'default':
      return __(
        'The default configuration will be applied to the domains being created, allowing to enjoy instant configuration, fast updates and interoperability within services.',
      );

    case process === 'create' && templateKind === 'custom':
      return __(
        'The following custom configuration will be applied to the domains being created.',
      );
    default:
      // create && predefined
      return __(
        "You have chosen a configuration template to apply to your domains being created. If we are unable to apply the template for a technical reason related to registry rules, we will apply default values and inform you on the domain's overview page.",
      );
  }
}

type Props = {
  templateName: ?string,
  process: CreateOrTransfer,
  changeURL: string | false,
  removeLink: boolean,
  templateKind: TemplateKind,
  configuration: CartConfiguration,
};

export default function Message({
  templateName,
  process,
  changeURL,
  removeLink,
  templateKind,
  configuration,
}: Props): React.Node {
  const translator = useTranslator();

  const description = getDescription({
    translator,
    process,
    templateKind,
  });

  const name =
    templateKind === 'default' && process === 'transfer'
      ? translator.__('No predefined configuration')
      : templateName || translator.__('Gandi default configuration');

  return (
    <>
      <p>{description}</p>
      {/* {description} */}
      <p>
        <BottomLinks
          name={name}
          changeURL={changeURL}
          removeLink={removeLink}
          configuration={configuration}
        />
      </p>
    </>
  );
}
